/* eslint-disable no-param-reassign */
import React, { useRef } from 'react';
import navigationLocker from '../../../../classes/navigationLocker';
import store from '../../../../store';
import { updateSessionState } from '../../../../store/appStateSlice';
import debugDispatcher from '../../../../utils/debugDispatcher';
import {
  TGetTabsController,
  TTabRenderer,
  Tab,
  TabsController,
} from '@apia/components';
import { closeMainMenu, openMainMenu } from '../Menu';
import { IOpenTabOptions } from '../tabsController';
import { observer } from 'mobx-react-lite';

export const handleDeadSession = (dispatch?: boolean) => {
  window.tabsController?.closeAll(true, true);
  navigationLocker.unlock();
  if (dispatch !== false)
    store.dispatch(updateSessionState({ isLog: false, message: '' }));
};
let reloadingParent = false;
window.addEventListener('beforeunload', () => {
  reloadingParent = true;
  setTimeout(() => {
    reloadingParent = false;
  }, 100);
});

const undefinedFunction = () =>
  new Proxy(
    {},
    {
      get(target, prop) {
        if (target[prop as keyof typeof target])
          return target[prop as keyof typeof target];
        return () => {};
      },
    },
  );
export interface ITabContainer {
  activeTab?: Tab['state'];
  addNewTab: (
    title: string,
    url: string,
    options?: IOpenTabOptions,
  ) => Promise<string>;
  addNewTabs: (args: ITab[]) => void;
  changeTabTitle: (tabId: string, title: string) => void;
  getHandler: () => TabsController;
  getTab: (tabId: string) => Tab | undefined;
  hasClass: () => boolean;
  removeActiveTab: (force?: boolean) => void;
  removeTabByID: (tab: string, force?: boolean) => void;
}

export function getTabContainer() {
  const tabsController = getIframe().getReactTabsController?.();
  if (tabsController) return tabsController;

  const windowTop = window.top;
  const isIniFrame = windowTop != null && windowTop.document != null;
  let result: HTMLElement | typeof window.tabsController | null =
    document.getElementById('tabContainer');
  if (result === null && isIniFrame)
    result =
      windowTop.document.getElementById('tabContainer') ||
      windowTop.tabsController;

  return result as unknown as ITabContainer;
}

export function getIframe() {
  return window.frameElement as HTMLIFrameElement;
}

interface ITab {
  fncId: number;
  tabId: string;
  title: string;
  url: string;
}

const tabContainer: ReturnType<TGetTabsController> = {
  addNewTab: (title, url, options) =>
    (window.top ?? window).tabsController.openTabByUrl(title, url, options),
  addNewTabs: (tabs) => {
    tabs.forEach((current) =>
      (window.top ?? window).tabsController.openTabByUrl(
        current.title,
        current.url,
        {
          funcId: current.fncId,
        },
      ),
    );
  },
  changeTabTitle: (tabId = window.TAB_ID, title) => {
    const tab = tabContainer.getTab(tabId);
    if (tab) {
      tab.state.label = title;
    }
  },
  getHandler() {
    return (window.top ?? window).tabsController.getHandler();
  },
  getTab(tabId = window.TAB_ID) {
    return (window.top ?? window).tabsController?.handler?.getTab(tabId);
  },
  hasClass() {
    return false;
  },
  removeActiveTab: (force?: boolean) => {
    (window.top ?? window).tabsController.closeActiveTab(force);
  },
  removeTabByID: (tabId = window.TAB_ID, force) =>
    void (window.top ?? window).tabsController?.handler.closeTab(tabId, force),
};

function initTabMethodsAndEvents(iframe: HTMLIFrameElement, tab: Tab) {
  iframe.tabId = tab.id;

  if (!document.querySelector('#tabContainer')) {
    const tabContainerElement = document.createElement('div');
    tabContainerElement.id = 'tabContainer';
    document.body.append(tabContainerElement);
    const castedTabContainer = tabContainerElement as unknown as ITabContainer;

    Object.assign(castedTabContainer, tabContainer);
    Object.defineProperty(castedTabContainer, 'activeTab', {
      configurable: true,
      get: () => (window.top ?? window).tabsController.getActiveTab()?.state,
    });
  }

  const handleKeyDown = (ev: KeyboardEvent) => {
    if (ev.code === 'F2') openMainMenu();
    if (ev.code === 'Escape') closeMainMenu();
  };

  function setWindowEvents() {
    const killSession =
      iframe.contentDocument?.querySelector('result > code')?.innerHTML ===
        '-1' &&
      !!iframe.contentDocument?.querySelector(
        'exceptions exception[text="Not Logged"]',
      );
    if (killSession) {
      handleDeadSession();
    }
    try {
      iframe.contentWindow?.addEventListener('keydown', handleKeyDown);
      iframe.contentWindow?.addEventListener('mousedown', closeMainMenu);
    } catch (e) {
      e;
    }

    function setIsNotLoading() {
      tab.state.isLoading = false;
    }

    if (iframe.contentWindow) {
      try {
        iframe.contentWindow.requestAnimationFrame(() => {
          if (iframe.contentWindow?.onScreenLockReleasedFirstTime) {
            iframe.contentWindow.onScreenLockReleasedFirstTime(() => {
              setIsNotLoading();
            });
          } else setIsNotLoading();
        });
        iframe.contentWindow.onbeforeunload = () => {
          if (!reloadingParent) {
            iframe.canRemoveTab = () => true;
            if (navigationLocker.isLocked) {
              if (!iframe.contentWindow?.avoidLoadEvents)
                tab.state.isLoading = true;
            }
          }
        };
      } catch (e) {
        e;
        setIsNotLoading();
      }
    }
  }
  iframe.handleDeadSession = handleDeadSession;
  iframe.getReactBodyController = () => {
    return new Proxy(
      {},
      {
        set(_, prop, value) {
          if (prop === 'canRemoveTab') {
            iframe.canRemoveTab = value as () => boolean;
          }
          if (prop === 'onFocusTab') {
            iframe.onFocusTab = value as () => unknown;
          }
          return true;
        },
      },
    );
  };
  iframe.getReactTabsController = () =>
    new Proxy(tabContainer, {
      get(target, prop) {
        if (prop in target) return target[prop as keyof typeof target];
        switch (prop) {
          case 'activeTab': {
            return window.tabsController.getActiveTab()?.state;
          }
          default:
            console.warn(
              `The method tabController.${prop as string} is not implemented`,
            );
            return () => {};
        }
      },
    });

  iframe.erase = undefinedFunction;
  iframe.fireEvent = undefinedFunction;
  iframe.get = undefinedFunction;
  iframe.hasClass = () => false;
  iframe.set = () => {};

  tab.state.onBeforeClose = async () => {
    try {
      const secondResult = iframe.canRemoveTab?.() ?? true;
      return secondResult;
    } catch (e) {
      e;
      return true;
    }
  };

  iframe.onBeforeClose = (callback) => {
    if (iframe.contentWindow) iframe.canRemoveTab = callback;
  };
  iframe.onload = setWindowEvents;

  return () => {
    iframe?.contentWindow?.removeEventListener('keydown', handleKeyDown);
    iframe?.contentWindow?.removeEventListener('mousedown', closeMainMenu);
  };
}

const ContentRenderer: TTabRenderer = observer(({ tab }) => {
  const hasOpen = useRef(tab.state.isOpen || !tab.state.lazy);
  if (tab.state.isOpen) hasOpen.current = true;

  return (
    <iframe
      className={tab.state.isOpen ? 'active' : undefined}
      ref={(el) => {
        if (el instanceof HTMLElement && el.contentWindow) {
          initTabMethodsAndEvents(el, tab);
        }
      }}
      src={hasOpen.current ? tab.state.additionalProps.url : undefined}
      title={tab.state.label}
    />
  );
});

debugDispatcher.on(
  'unlockTabs',
  () => {
    window.tabsController?.handler.tabsList?.forEach((current) => {
      current.state.isLoading = false;
    });
  },
  'Permite bajar el spinner de los tabs, dd.unlockTabs()',
);

export default React.memo(ContentRenderer);
